import { ChangeDetectorRef, Component, ElementRef, Input, signal, ViewChild } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { WeatherService } from 'src/app/_services/weather.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GolfCourseModel } from 'src/app/_interfaces/golf/golf-course';
import { CurrentWeather, Forecast, GolfWeatherModel, HourData, WeatherLocation } from 'src/app/_interfaces/golf/weather';
import { AppStringsService } from 'src/app/_services';
import { AppStringComponent } from '../app-string/app-string.component';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';

@Component({
    selector: 'app-weather',
    imports: [AppStringComponent, MatProgressSpinnerModule, MatFormFieldModule,
        MatSelectModule, LoadingIndicatorComponent
    ],
    templateUrl: './weather.component.html',
    styleUrl: './weather.component.scss'
})
export class WeatherComponent {
    @Input() set Course(courses: GolfCourseModel[] | undefined) {
        if (courses) {
            this.primary_course = courses.find((c) => c.primary_course === true && this.hasValidCoordinates(c)) ?? null;

            if (this.primary_course) {
                this.get__weather()
            } else if (!this.primary_course) {
                this.loading.set(false)
            }
        }
    }

    @ViewChild('hourScroller') hourScroller!: ElementRef<HTMLDivElement>;

    public primary_course!: GolfCourseModel | null
    public current_weather: CurrentWeather = {} as CurrentWeather
    public location!: WeatherLocation;
    public forecast!: Forecast;
    public hourly: HourData[] = [];

    public loading = signal(true);
    public viewMode: 'current' | 'hourly' = 'current';
    public useImperial: boolean = true;

    constructor(
        private weatherAPI: WeatherService,
        private cdr: ChangeDetectorRef,
        private appStrings: AppStringsService
    ) {
        this.useImperial = this.prefersImperial()
    }

    private t(key: string): string {
        return this.appStrings?.getAppString?.(key) ?? this.appStrings?.cache?.get?.(key) ?? key;
    }

    private fmtLocalizedNumber(template: string, value: number): string {
        const n = Math.round(value ?? 0);
        if (template.includes('%d%%')) {
            return template.replace('%d%%', `${n}%`);
        }
        if (template.includes('%d')) {
            return template.replace('%d', String(n));
        }
        if (template.includes('%.0f')) {
            return template.replace('%.0f', String(n));
        }

        return template;
    }

    formatRainProbability(p?: number): string {
        return this.fmtLocalizedNumber(this.t('weather.rainprobability'), p ?? 0);  // '%d%%'
    }

    // Decide imperial vs metric on web. Imperial
    // for US/LR/MM; everyone else sees metric.
    private prefersImperial(): boolean {
        if (typeof navigator === 'undefined') return true;
        const lang = navigator.language || 'en-US';
        const region = lang.split('-')[1]?.toUpperCase() || 'US';
        return ['US', 'LR', 'MM'].includes(region);
    }

    formatTemp(tempF?: number, tempC?: number): string {
        if (this.useImperial) {
            const f = (typeof tempF === 'number') ? tempF : (typeof tempC === 'number' ? (tempC * 9) / 5 + 32 : 0);
            return this.fmtLocalizedNumber(this.t('weather.fahrenheit.degree'), f);   // '%.0f°F'
        } else {
            const c = (typeof tempC === 'number') ? tempC : (typeof tempF === 'number' ? (tempF - 32) * 5 / 9 : 0);
            return this.fmtLocalizedNumber(this.t('weather.celsius.degree'), c);      // '%.0f°C'
        }
    }

    formatWind(windMph?: number, windKph?: number): string {
        if (this.useImperial) {
            const mph = (typeof windMph === 'number') ? windMph : (typeof windKph === 'number' ? windKph / 1.609344 : 0);
            return this.fmtLocalizedNumber(this.t('weather.wind.mph'), mph);          // 'Wind: %.0f mph'
        } else {
            const kph = (typeof windKph === 'number') ? windKph : (typeof windMph === 'number' ? windMph * 1.609344 : 0);
            return this.fmtLocalizedNumber(this.t('weather.wind.kph'), kph);          // 'Wind: %.0f km/h'
        }
    }

    formatHumidity(h?: number): string {
        return this.fmtLocalizedNumber(this.t('weather.humidity'), h ?? 0);         // 'Humidity %d%'
    }

    formatCloudCover(c?: number): string {
        return this.fmtLocalizedNumber(this.t('weather.cloudcover'), c ?? 0);       // 'Cloud Cover: %d%'
    }

    handle__weatherIcon(url?: string): string {
        return url ? `https:${url}` : ''
    }

    formatHourLocal(epochSeconds: number): string {
        const userTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const d = new Date(epochSeconds * 1000);
        const label = new Intl.DateTimeFormat(undefined, {
            hour: 'numeric',
            hour12: true,
            timeZone: userTZ
        }).format(d);
        return label.replace(' ', '').toUpperCase();
    }

    private scrollToCurrentHour(): void {
        if (!this.hourly?.length || !this.hourScroller) return;

        const nowSec = Math.floor(Date.now() / 1000);

        // Find the last hour <= now (current hour). If none, pick the closest future hour.
        let idx = -1;
        for (let i = 0; i < this.hourly.length; i++) {
            if (this.hourly[i].time_epoch <= nowSec) idx = i;
            else break;
        }
        if (idx === -1) {
            // all entries are in the future; pick the first
            idx = 0;
        }

        setTimeout(() => {
            const scroller = this.hourScroller.nativeElement;
            const target = scroller.querySelector<HTMLElement>(`[data-hour-index="${idx}"]`);
            if (!target) return;

            const left = target.offsetLeft - (scroller.clientWidth - target.clientWidth) / 2;
            // If we want the card to show s the first from left to right instead of being centered
            // const left = target.offsetLeft - 16;
            scroller.scrollTo({ left, behavior: 'instant' as ScrollBehavior });
        }, 0);
    }

    async get__weather() {
        if (!this.primary_course || !this.hasValidCoordinates(this.primary_course)) {
            console.error('no course or invalid coordinates')
            return
        };

        try {
            const lat = String(this.primary_course?.latitude);
            const lng = String(this.primary_course?.longitude);

            const res: GolfWeatherModel = await this.weatherAPI.get__weather__v2(lat, lng);

            this.current_weather = res?.current ?? {} as CurrentWeather;
            this.location = res?.location as WeatherLocation;
            this.forecast = res?.forecast as Forecast;
            this.hourly = this.forecast?.forecastday?.[0]?.hour ?? [];

            this.cdr.detectChanges();
            if (this.viewMode === 'hourly') this.scrollToCurrentHour();
            this.loading.set(false)
        } catch (err) {
            throw err;
        }
    }

    onChangeView(mode: 'current' | 'hourly'): void {
        if (this.viewMode === mode) return;

        this.viewMode = mode;

        if (mode === 'hourly') {
            this.cdr.detectChanges();
            this.scrollToCurrentHour();
        }
    }

    private hasValidCoordinates(course: GolfCourseModel): boolean {
        const lat = Number(course.latitude);
        const lon = Number(course.longitude);

        return (
            !isNaN(lat) &&
            !isNaN(lon) &&
            lat !== 0 &&
            lon !== 0
        );
    }

    windRotation(deg?: number): string {
        const n = Number(deg);
        return Number.isFinite(n) ? `rotate(${n}deg)` : 'rotate(0deg)';
    }
}
